import { FC, useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import type { AutoReturnReport } from '../autoReturnAndResend.types';
import {
    getReportDate,
    getReportId,
    getReportUserName,
    isReportSuccessful,
} from '../autoReturnAndResend.helpers';
import { dataGridSx, renderOverflowCell } from '../autoReturnAndResend.gridHelpers';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendReportsPanelProps {
    reports: AutoReturnReport[];
    onViewDetails: (report: AutoReturnReport) => void;
}

export const AutoReturnAndResendReportsPanel: FC<AutoReturnAndResendReportsPanelProps> = ({ reports, onViewDetails }) => {
    const { t } = useTranslation();

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'rpaRequestId',
                headerName: t('autoReturnAndResend.reports.columns.reportId'),
                flex: 1,
                minWidth: 160,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => renderOverflowCell(getReportId(params.row)),
            },
            {
                field: 'createDate',
                headerName: t('autoReturnAndResend.reports.columns.date'),
                flex: 1,
                minWidth: 160,
                headerAlign: 'center',
                align: 'center',
                valueGetter: (_value, row) => getReportDate(row),
            },
            {
                field: 'userName',
                headerName: t('autoReturnAndResend.reports.columns.processedBy'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => renderOverflowCell(getReportUserName(params.row)),
            },
            {
                field: 'status',
                headerName: t('autoReturnAndResend.reports.columns.status'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => {
                    const isCompleted = isReportSuccessful(params.row);

                    return (
                        <Chip
                            size="small"
                            color={isCompleted ? 'success' : 'error'}
                            label={
                                isCompleted
                                    ? t('autoReturnAndResend.reports.status.completed')
                                    : t('autoReturnAndResend.reports.status.failed')
                            }
                        />
                    );
                },
            },
            {
                field: 'detail',
                headerName: t('autoReturnAndResend.reports.columns.detail'),
                flex: 1,
                minWidth: 140,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <SCUIButton
                        text={t('autoReturnAndResend.reports.viewDetails')}
                        type="secondary"
                        sizeType="fixed"
                        size="s"
                        onClick={() => onViewDetails(params.row)}
                    />
                ),
            },
        ],
        [onViewDetails, t]
    );

    return (
        <Box className={ARAR.reportsSection}>
            <Box className={ARAR.panelGrid} sx={dataGridSx}>
                <SCUICustomDataGrid
                    getRowId={(row: AutoReturnReport) => getReportId(row)}
                    rows={reports}
                    columns={columns}
                    pageSize={10}
                    autoHeight
                    localeText={t('autoReturnAndResend.reports.noReports')}
                />
            </Box>
        </Box>
    );
};
